import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Combobox } from "@/components/ui/combobox";
import { User, ArrowRight, IdCard } from "lucide-react";
import { PackageData } from "@/pages/CreatePackage";
import { useClients } from "@/hooks/useClients";
import { useCompanies } from "@/hooks/useCompanies";
import { useDebounce } from "@/hooks/useDebounce";
import { StepWrapper } from "./StepWrapper";
interface ClientInfoStepSimpleProps {
  data: PackageData;
  onUpdate: (data: Partial<PackageData>) => void;
  onNext: () => void;
  completedSteps?: number[];
  onStepClick?: (step: number) => void;
  isStepRequired?: (step: number) => boolean;
}
export const ClientInfoStepSimple = ({
  data,
  onUpdate,
  onNext,
  completedSteps = [],
  onStepClick = () => {},
  isStepRequired = () => true
}: ClientInfoStepSimpleProps) => {
  // Fixed: Ensure hooks are always called unconditionally
  const [addClientToDB, setAddClientToDB] = useState(data.addClientToDB || false);

  // Always call hooks - never conditionally
  const clientsQuery = useClients();
  const companiesQuery = useCompanies();

  // Safe destructuring after hooks
  const clients = clientsQuery.data || [];
  const clientsLoading = clientsQuery.isLoading;
  const clientsError = clientsQuery.error;
  const companies = companiesQuery.data || [];
  const companiesLoading = companiesQuery.isLoading;
  const companiesError = companiesQuery.error;

  // فلترة العملاء الأفراد (بناءً على client_type وليس company_id)
  const individualClients = clients?.filter(client => (!data.clientType || data.clientType === 'individual') && client.name_ar && client.client_type === 'direct');

  // فلترة عملاء الشركات (العملاء المرتبطين بشركات كعملاء b2b)
  const companyClients = clients?.filter(client => client.name_ar && client.company_id && client.client_type !== 'direct');

  // فلترة الشركات
  const validCompanies = companies?.filter(company => company.name_ar);
  const handleNext = () => {
    console.log('Validating client data:', {
      clientType: data.clientType,
      clientPhone: data.clientPhone
    });
    if (!data.clientType) {
      alert("يرجى اختيار نوع العميل");
      return;
    }
    if (!data.clientPhone?.trim()) {
      alert("يرجى إدخال رقم الهاتف");
      return;
    }
    console.log('Validation passed, proceeding to next step');
    onNext();
  };
  const handleClientSelect = (clientId: string) => {
    const selectedClient = clients.find(c => c.id === clientId);
    if (selectedClient) {
      onUpdate({
        clientName: selectedClient.name_ar,
        clientEmail: selectedClient.mobile || '',
        clientPhone: selectedClient.mobile || '',
        clientAddress: selectedClient.additional_info || '',
        companyId: selectedClient.company_id,
        companyName: selectedClient.company
      });
    }
  };
  const handleCompanySelect = (companyId: string) => {
    const selectedCompany = companies.find(c => c.id === companyId);
    if (selectedCompany) {
      onUpdate({
        companyId: companyId,
        companyName: selectedCompany.name_ar
      });
    }
  };
  return <StepWrapper currentStep={1} completedSteps={completedSteps} onStepClick={onStepClick} isStepRequired={isStepRequired}>
      <div className="w-full max-w-full">
        {/* معلومات العميل */}
        <Card className="shadow-lg border-0">
          <CardHeader className="border-b bg-gradient-to-r from-primary/5 to-primary/10 pb-4">
            <CardTitle className="flex items-center gap-3 text-xl">
              <div className="p-2 bg-primary rounded-lg shadow-sm">
                <IdCard className="w-5 h-5 text-white" />
              </div>
              معلومات العميل
            </CardTitle>
          </CardHeader>
          
          <CardContent className="p-6">
            <div className="space-y-6">
              {/* نوع العميل */}
              <div className="space-y-3">
                <Label className="text-sm font-medium text-foreground">نوع العميل *</Label>
                <div className="grid grid-cols-2 gap-4">
                  <Button
                    type="button"
                    variant={data.clientType === 'individual' ? 'default' : 'outline'}
                    onClick={() => onUpdate({ clientType: 'individual' })}
                    className="h-12 flex items-center justify-center gap-2"
                  >
                    <User className="w-4 h-4" />
                    عميل مباشر
                  </Button>
                  <Button
                    type="button"
                    variant={data.clientType === 'b2b' ? 'default' : 'outline'}
                    onClick={() => onUpdate({ clientType: 'b2b' })}
                    className="h-12 flex items-center justify-center gap-2"
                  >
                    <IdCard className="w-4 h-4" />
                    عميل شركات
                  </Button>
                </div>
              </div>

              {/* اختيار العميل الموجود */}
              {data.clientType === 'b2b' && <div className="space-y-3">
                  <Label className="text-sm font-medium text-foreground">اختيار شركة موجودة</Label>
                  <Select value={data.companyId || ''} onValueChange={handleCompanySelect}>
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="اختر شركة موجودة أو أدخل بيانات جديدة" />
                    </SelectTrigger>
                    <SelectContent>
                      {companiesLoading ? <SelectItem value="loading" disabled>جاري التحميل...</SelectItem> : <>
                          {validCompanies?.length > 0 ? validCompanies.map(company => <SelectItem key={company.id} value={company.id}>
                                {company.name_ar}
                              </SelectItem>) : <SelectItem value="no-companies" disabled>لا توجد شركات</SelectItem>}
                        </>}
                    </SelectContent>
                  </Select>
                </div>}

              {data.clientType === 'individual' && <div className="space-y-3">
                  <Label className="text-sm font-medium text-foreground">اختيار عميل موجود</Label>
                  <Combobox options={individualClients?.map(client => ({
                label: `${client.name_ar} - ${client.mobile || 'لا يوجد رقم'}`,
                value: client.id
              })) || []} value="" onChange={handleClientSelect} placeholder="اختر عميل موجود أو أدخل بيانات جديدة" searchPlaceholder="ابحث بالاسم أو رقم الجوال..." emptyText="لا توجد نتائج" className="h-11" />
                </div>}

              {/* معلومات العميل الأساسية - في سطر واحد */}
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="clientName" className="text-sm font-medium text-foreground">
                    اسم العميل
                  </Label>
                  <Input id="clientName" value={data.clientName || ''} onChange={e => {
                  console.log('Client name changed:', e.target.value);
                  onUpdate({
                    clientName: e.target.value
                  });
                }} placeholder="أدخل اسم العميل (اختياري)" className="h-11" />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="clientPhone" className="text-sm font-medium text-foreground">
                    رقم الهاتف *
                  </Label>
                  <Input id="clientPhone" value={data.clientPhone || ''} onChange={e => {
                  console.log('Client phone changed:', e.target.value);
                  onUpdate({
                    clientPhone: e.target.value
                  });
                }} placeholder="+966 5XX XXX XXX" className="h-11" required />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="clientEmail" className="text-sm font-medium text-foreground">
                    البريد الإلكتروني
                  </Label>
                  <Input id="clientEmail" type="email" value={data.clientEmail || ''} onChange={e => onUpdate({
                  clientEmail: e.target.value
                })} placeholder="client@example.com" className="h-11" />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="clientAddress" className="text-sm font-medium text-foreground">
                    العنوان
                  </Label>
                  <Input id="clientAddress" value={data.clientAddress || ''} onChange={e => onUpdate({
                  clientAddress: e.target.value
                })} placeholder="أدخل عنوان العميل" className="h-11" />
                </div>
              </div>

              {/* خيار إضافة العميل للنظام */}
              <div className="p-4 bg-muted/30 rounded-lg border">
                <div className="flex items-center space-x-2 space-x-reverse">
                  <Checkbox id="addClientToDB" checked={addClientToDB} onCheckedChange={checked => setAddClientToDB(checked as boolean)} />
                  <Label htmlFor="addClientToDB" className="text-sm font-medium cursor-pointer">
                    💾 إضافة هذا العميل إلى قسم العملاء في النظام
                  </Label>
                </div>
                
              </div>
            </div>
          </CardContent>
        </Card>

        {/* زر التالي */}
        <Card className="mt-4 shadow-sm border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              
              <Button onClick={handleNext} className="flex items-center gap-2 h-11 px-[50px] py-0 mx-0">
                التالي: تفاصيل الرحلة والبرنامج
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </StepWrapper>;
};